<template>
  <div class="commit-card">
    <div class="commit-card__head">
      <div class="commit-card__id">
        <h4 class="commit-card__name">{{ token.name }}</h4>
        <span class="commit-card__symbol">{{ token.symbol }}</span>
      </div>
      <span class="commit-card__badge" :class="'commit-card__badge--' + state.tone">
        {{ state.label }}
      </span>
    </div>

    <p v-if="state.detail" class="commit-card__detail">{{ state.detail }}</p>

    <!-- The outcome, said in numbers rather than left to be inferred from the rows below. A
         scale-back is the moment an investor is most likely to think something went wrong, so
         the arithmetic that produced it is on the card rather than in a support ticket. -->
    <div v-if="outcome" class="commit-card__outcome">
      <p>{{ outcome }}</p>
      <p v-if="finalLevelLabel" class="commit-card__outcome-sub">
        Your bid: {{ bidAtLabel }} · Final {{ noun }}: {{ finalLevelLabel }}
      </p>
    </div>

    <dl class="commit-card__rows">
      <div>
        <dt>You bid</dt>
        <dd>{{ money(commitment.amount) }}{{ bidAt }}</dd>
      </div>
      <!-- Only while the money is still tied up. Once released, showing a held figure would
           suggest it still is. -->
      <div v-if="state.held">
        <dt>Held from your wallet</dt>
        <dd>{{ money(commitment.hold_amount) }}</dd>
      </div>
      <div v-if="allottedAmount > 0">
        <dt>Allotted</dt>
        <dd>
          {{ money(allottedAmount) }}
          <span v-if="commitment.allotted_units" class="commit-card__sub">
            {{ convertToLocale(commitment.allotted_units) }} units<template v-if="allottedTerms">
              at {{ allottedTerms }}</template>
          </span>
        </dd>
      </div>
      <div v-if="commitment.refund_amount">
        <dt>Released to your wallet</dt>
        <dd>{{ money(commitment.refund_amount) }}</dd>
      </div>
      <div v-if="closeLabel">
        <dt>{{ state.canAmend ? "Closes" : "Closed" }}</dt>
        <dd>{{ closeLabel }}</dd>
      </div>
    </dl>

    <div v-if="state.canAmend" class="commit-card__actions">
      <button class="commit-card__btn" @click="$emit('amend', commitment)">Change bid</button>
      <button class="commit-card__btn commit-card__btn--quiet" @click="$emit('withdraw', commitment)">
        Withdraw
      </button>
    </div>

    <!-- The bid changed while the book was open, so the current figures are not the whole
         story. Sponsors and regulators see this trail; the investor should too. -->
    <details v-if="commitment.amendments && commitment.amendments.length" class="commit-card__history">
      <summary>{{ commitment.amendments.length }} previous version{{ commitment.amendments.length === 1 ? "" : "s" }}</summary>
      <ul>
        <li v-for="(a, i) in commitment.amendments" :key="i">
          {{ money(a.amount) }}<template v-if="a.bid_price"> at {{ money(a.bid_price) }}</template>
          <template v-else-if="a.bid_rate"> at {{ a.bid_rate }}%</template>
        </li>
      </ul>
    </details>
  </div>
</template>

<script>
import { convertToLocale, getCurrencySymbol } from "@/utils/helpers.js";
import { formatOfferMoment, resolveCommitmentState } from "@/utils/offering";

export default {
  name: "CommitmentCard",

  emits: ["amend", "withdraw"],

  props: {
    commitment: { type: Object, required: true },
  },

  computed: {
    token() {
      return this.commitment.token || {};
    },
    state() {
      return resolveCommitmentState(this.commitment);
    },
    bidAt() {
      if (this.commitment.is_strike_bid) return " at the final price";
      if (this.commitment.bid_price) return ` at ${this.money(this.commitment.bid_price)}`;
      if (this.commitment.bid_rate) return ` at ${this.commitment.bid_rate}%`;
      return "";
    },

    isRateBased() {
      // Matches the API's isRateBased (debt and fixed interest only). A fund clears at a
      // price, so showing its bid as a rate misreports what was actually bid.
      return ["Debt", "Fixed Interest"].includes(this.token.investment_type);
    },
    noun() {
      return this.isRateBased ? "rate" : "price";
    },
    bidAtLabel() {
      if (this.commitment.is_strike_bid) return `whatever the book settled at`;
      if (this.commitment.bid_price) return this.money(this.commitment.bid_price);
      if (this.commitment.bid_rate) return `${this.commitment.bid_rate}%`;
      return "—";
    },
    // Read off the token, not the commitment: a rejected bid has no allotted price of its own,
    // and the final level is precisely what it needs to be compared against.
    finalLevelLabel() {
      if (this.isRateBased) {
        return this.token.final_rate != null ? `${this.token.final_rate}%` : "";
      }
      return this.token.final_price != null ? this.money(this.token.final_price) : "";
    },
    /**
     * What was actually allotted.
     *
     * `allotted_amount` is on the Commitment schema but nothing writes it: settlement's
     * `finaliseCommitment` persists only `status` and `refund_amount`, and the real figures
     * live on the AllotmentLine, which this endpoint does not join.
     *
     * Both cases are recoverable from what IS stored, exactly rather than approximately:
     * a full allotment is the amount applied for, and a partial one is that amount less what
     * came back. Derived here rather than printed as undefined, and it simply defers to the
     * stored value the day one appears.
     */
    allottedAmount() {
      const stored = this.commitment.allotted_amount;
      if (stored !== null && stored !== undefined) return Number(stored);

      const applied = Number(this.commitment.amount || 0);
      if (this.commitment.status === "ALLOTTED") return applied;
      if (this.commitment.status === "PARTIALLY_ALLOTTED") {
        return Math.max(applied - Number(this.commitment.refund_amount || 0), 0);
      }
      return 0;
    },

    /**
     * What the units were won on: a price on equity, a rate on debt.
     *
     * Settlement writes whichever applies and omits the other, and it writes neither when
     * nothing was allotted — a priced-out bid still sits against a line carrying the level the
     * book cleared at, and showing that would tell an investor they were allotted at a price
     * when they were allotted nothing. So absence here is meaningful and stays blank.
     */
    allottedTerms() {
      if (this.commitment.allotted_rate != null) return `${this.commitment.allotted_rate}%`;
      if (this.commitment.allotted_at_price != null) {
        return this.money(this.commitment.allotted_at_price);
      }
      return "";
    },

    fillPercent() {
      const applied = Number(this.commitment.amount || 0);
      if (!applied) return 0;
      return Math.round((this.allottedAmount / applied) * 100);
    },
    outcome() {
      const status = this.commitment.status;

      if (status === "ALLOTTED") {
        return `You were allotted everything you asked for: ${this.money(this.allottedAmount)}.`;
      }

      if (status === "PARTIALLY_ALLOTTED") {
        return `You applied for ${this.money(this.commitment.amount)} and were allotted ${this.money(
          this.allottedAmount
        )}, ${this.fillPercent}% of your bid. The offer was oversubscribed, so every qualifying bid was scaled back. ${this.money(
          this.commitment.refund_amount
        )} has been released to your wallet.`;
      }

      if (status === "REJECTED") {
        return this.commitment.is_strike_bid
          ? `Your bid was not allotted. ${this.money(this.commitment.amount)} has been released to your wallet.`
          : `Your bid did not qualify at the final ${this.noun}, so none of it was allotted. ${this.money(
              this.commitment.amount
            )} has been released to your wallet.`;
      }

      return "";
    },
    closeLabel() {
      return this.token.offer_closes_at
        ? formatOfferMoment(this.token.offer_closes_at, this.token.market_timezone)
        : "";
    },
  },

  methods: {
    convertToLocale,
    money(value) {
      const symbol = getCurrencySymbol(this.commitment.currency || this.token.currency || "USD");
      return `${symbol}${convertToLocale(Number(value) || 0)}`;
    },
  },
};
</script>

<style lang="scss" scoped>
$text-primary: #0f172a;
$text-secondary: #475569;
$text-muted: #64748b;
$border-color: #e2e8f0;
$bg-subtle: #f8fafc;
$accent: #4d84ff;

.commit-card {
  border: 1px solid $border-color;
  border-radius: 10px;
  padding: 16px;
  background: #fff;

  &__head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 8px;
  }

  &__name {
    font-size: 15px;
    font-weight: 600;
    color: $text-primary;
    margin: 0;
  }

  &__symbol {
    font-size: 12px;
    color: $text-muted;
  }

  &__badge {
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;

    &--neutral {
      background: #f1f5f9;
      color: $text-muted;
    }
    &--info {
      background: #e0e7ff;
      color: #4338ca;
    }
    &--active {
      background: #dbeafe;
      color: #1d4ed8;
    }
    &--success {
      background: #d1fae5;
      color: #059669;
    }
  }

  &__detail {
    font-size: 12px;
    line-height: 1.6;
    color: $text-secondary;
    margin: 0 0 12px;
  }

  &__outcome {
    margin: 0 0 14px;
    padding: 12px 14px;
    background: $bg-subtle;
    border: 1px solid $border-color;
    border-radius: 8px;

    p {
      margin: 0;
      font-size: 12px;
      line-height: 1.6;
      color: $text-primary;
    }
  }

  &__outcome-sub {
    margin-top: 6px !important;
    color: $text-secondary !important;
    font-variant-numeric: tabular-nums;
  }

  &__rows {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 0 0 14px;
    padding-top: 12px;
    border-top: 1px solid $border-color;

    > div {
      display: flex;
      justify-content: space-between;
      gap: 12px;
    }

    dt {
      font-size: 12px;
      color: $text-muted;
    }

    dd {
      font-size: 13px;
      font-weight: 500;
      color: $text-primary;
      margin: 0;
      text-align: right;
      font-variant-numeric: tabular-nums;
    }
  }

  &__sub {
    display: block;
    font-weight: 400;
    font-size: 12px;
    color: $text-secondary;
  }

  &__actions {
    display: flex;
    gap: 8px;
  }

  &__btn {
    padding: 8px 14px;
    border-radius: 6px;
    border: 1px solid $accent;
    background: #fff;
    color: $accent;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;

    &--quiet {
      border-color: $border-color;
      color: $text-secondary;
    }
  }

  &__history {
    margin-top: 12px;
    font-size: 12px;

    summary {
      cursor: pointer;
      color: $text-muted;
    }

    ul {
      margin: 8px 0 0;
      padding-left: 16px;
      color: $text-secondary;
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
  }
}
</style>
